import { useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { CollectionPageChildProps } from "../CollectionPage";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { patchCollection, sendOnboardingEmail } from "@/services/collection";
import { fireTagManagerEvent } from "@/services/tagmanager";
import { fireJuneEvent } from "@/components/analytics/JuneAnalytics";
import { patchGroup } from "@/services/groups";
import { useAuthContext } from "@/context/AuthContext";
import { infoStepperOutletContext } from "../InfoStepper";

export default function HotelChain({
  collection,
  group,
  onRefresh,
}: CollectionPageChildProps) {
  const { setProgress } = useOutletContext<infoStepperOutletContext>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isIndependent, setIsIndependent] = useState<boolean>(
    group?.name === collection?.name,
  );
  const { userEmail } = useAuthContext();
  const [chainName, setChainName] = useState("");

  useEffect(() => {
    setProgress(100);
  }, []);

  const onSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    try {
      if (isIndependent) {
        await patchCollection({
          collectionId: collection?._id,
          isOnboardingComplete: true,
        });
      } else {
        await patchGroup({
          groupId: collection?.groupId,
          name: chainName,
        });
        await patchCollection({
          collectionId: collection?._id,
          isOnboardingComplete: true,
        });
      }

      await sendOnboardingEmail(collection?._id || "");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      await onRefresh();
      fireTagManagerEvent("onboarding_complete_profile", {
        name: collection?.name,
        email: userEmail,
      });
      fireJuneEvent("create_collection", {
        name_collection: collection?.name,
      });

      navigate(`/collection/${collection?._id}/taks`, {
        state: { openModal: true },
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mt-5 mb-10">
        <p className="text-lg font-medium text-gray-600">
          {t("onboarding.hotel_chain_subtitle")}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto">
        <RadioGroup
          value={isIndependent.toString()}
          onValueChange={(value) => {
            setIsIndependent(value === "true");
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="false" id="independent" />
            <label
              htmlFor="independent"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("onboarding.yes")}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="true" id="not-independent" />
            <label
              htmlFor="not-independent"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("onboarding.no")}
            </label>
          </div>
        </RadioGroup>

        {!isIndependent && (
          <div className="flex flex-col space-y-2 mb-6 mt-11">
            <Label htmlFor="chainName">{t("onboarding.chain_name")}*</Label>
            <Input
              id="chainName"
              placeholder={t("onboarding.enter_chain_name")}
              value={chainName}
              onChange={(e) => setChainName(e.target.value)}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <div className="flex flex-row justify-between">
          <Button
            className=""
            type="button"
            onClick={() =>
              navigate(`/collection/${collection?._id}/infos/hotelbenefits`)
            }
          >
            {t("auth.back")}
          </Button>
          {!isLoading ? (
            <Button
              variant="takyon"
              type="submit"
              onClick={onSubmit}
              disabled={!isIndependent && !chainName}
            >
              {t("buttons.save_and_continue")}
            </Button>
          ) : (
            <Button variant="takyon" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("auth.loading")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
